import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { WizardComponent, WizardStepComponent } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RestoreHyperVModelForStepsHelper } from 'wizards/helpers/restore-hyper-v-helpers/restore-hyper-v-model-for-steps-helper';
import { RestoreHyperVPlanFromStepsHelpers } from 'wizards/helpers/restore-hyper-v-helpers/restore-hyper-v-plan-from-steps-helpers';
import { RestoreType } from 'wizards/models/restore-point-models';
import { RestoreHyperVFormModel } from 'wizards/models/restoreHyperV/restore-hyper-v-form-model';
import { RestoreAsDiskEnum } from 'wizards/models/type-data-models';
import { WizardHelpLink } from 'wizards/remote-management-wizards/strategy/wizard-help-link.class';
import { WizardBaseClass } from 'wizards/remote-management-wizards/wizard-base.class';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { WizardsServicesHubService } from 'wizards/services/wizards-services-hub.service';
import { StepLoadInfo } from 'wizards/steps/StepBase.class';
import { BackupToRestoreStepComponent } from 'wizards/steps/backup-to-restore-step/backup-to-restore-step.component';
import { EncryptionOptionsStepComponent } from 'wizards/steps/encription-options-step/encryption-options-step.component';
import { NotificationsStepComponent } from 'wizards/steps/notifications-step/notifications-step.component';
import { DestinationVMStepComponent } from 'wizards/steps/only-restore-steps/destination-vm-step/destination-vm-step.component';
import { RestoreDisksStepComponent } from 'wizards/steps/only-restore-steps/restore-disks-step/restore-disks-step.component';
import { TypeDataStepComponent } from 'wizards/steps/only-restore-steps/type-data-step/type-data-step.component';
import { PlanNameStepComponent } from 'wizards/steps/plan-name-step/plan-name-step.component';
import { PrePostActionsStepComponent } from 'wizards/steps/pre-post-actions-step/pre-post-actions-step.component';
import { RestorePointStepComponent } from 'wizards/steps/restore-point-step/restore-point-step.component';
import { RestoreSourceStepComponent } from 'wizards/steps/restore-source-step/restore-source-step.component';
import { ScheduleAdvancedStepComponent } from 'wizards/steps/schedule-advanced-step/schedule-advanced-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { SelectComputerStepComponent } from 'wizards/steps/select-computer-step/select-computer-step.component';
import { WhereBackupStepComponent } from 'wizards/steps/where-backup-step/where-backup-step.component';

type RestoreHyperVWizardHelpDocumentationLinks =
  | 'welcome'
  | 'storage'
  | 'selectComputer'
  | 'selectBackup'
  | 'selectPoint'
  | 'source'
  | 'typeOfData'
  | 'disks'
  | 'destination'
  | 'encryption'
  | 'schedule'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-restore-hyper-v-wizard',
  templateUrl: './restore-hyper-v-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => RestoreHyperVWizardComponent)
    }
  ],
  imports: [
    AsyncPipe,
    BackupToRestoreStepComponent,
    CommonModule,
    DestinationVMStepComponent,
    EncryptionOptionsStepComponent,
    FormsModule,
    NotificationsStepComponent,
    PlanNameStepComponent,
    PrePostActionsStepComponent,
    RestoreDisksStepComponent,
    RestorePointStepComponent,
    RestoreSourceStepComponent,
    ScheduleAdvancedStepComponent,
    ScheduleStepComponent,
    SelectComputerStepComponent,
    TypeDataStepComponent,
    WhereBackupStepComponent,
    WizardComponent,
    WizardStepComponent,
    I18NextPipe
  ]
})
export class RestoreHyperVWizardComponent extends WizardBaseClass {
  i18nPipe: I18NextPipe;

  public readonly restoreType = RestoreType;
  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  public readonly restorePointTitle = this.i18nPipe.transform('wizards:restore_point_title');

  public helpLink: WizardHelpLink<RestoreHyperVWizardHelpDocumentationLinks>;

  constructor() {
    const services = inject(WizardsServicesHubService);
    const i18nPipe = inject(I18NextPipe);
    const stepService = inject(WizardStepsService);
    const helpService = inject(HelpService);

    super();
    this.i18nPipe = i18nPipe;

    this.FormModel = new RestoreHyperVFormModel(this.isNBF);

    this.initSubscribes();
  }

  protected updateStepData(plan, isDefault?: boolean): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) return this.setDefaultValue();

    this.FormModel = RestoreHyperVModelForStepsHelper.restoreVMModelFromPlan(plan, this.defaultSettings());

    this.stepsManager.showEncryptionOptionsStepIfNeed();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected loadInfoHandlerFinally(event?: StepLoadInfo): void {
    this.cdr.detectChanges();
  }

  restoreAsChangeHandler(type: RestoreAsDiskEnum): void {
    if (type === RestoreAsDiskEnum.Virtual) {
      this.mainService.type = PlanTypes.RestoreHyperVToCustom;
      this.planType = PlanTypes.RestoreHyperVToCustom;
    } else {
      this.mainService.type = PlanTypes.RestoreHyperV;
      this.planType = PlanTypes.RestoreHyperV;
    }
    this.cdr.detectChanges();
  }

  protected getPlanForSaving(): any {
    return RestoreHyperVPlanFromStepsHelpers.getUpdatedPlan(
      this.loadedPlan.Plan || this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings()
    );
  }

  getRAWHelpLinks(): Record<RestoreHyperVWizardHelpDocumentationLinks, string> {
    return {
      welcome: 'restore/hyper-v/restore-console#planname',
      storage: 'restore/hyper-v/restore-console#storage',
      selectComputer: 'restore/hyper-v/restore-console#comp',
      selectBackup: 'restore/hyper-v/restore-console#backup',
      selectPoint: 'restore/hyper-v/restore-console#rp',
      source: 'restore/hyper-v/restore-console#rs',
      typeOfData: 'restore/hyper-v/restore-console#restoreas',
      disks: 'restore/hyper-v/restore-console#partitions',
      destination: 'restore/hyper-v/restore-console#destination',
      encryption: 'restore/hyper-v/restore-console#encryption',
      schedule: 'restore/hyper-v/restore-console#schedule',
      prePost: 'restore/hyper-v/restore-console#prepost',
      notification: 'restore/hyper-v/restore-console#notification'
    };
  }
}
